import { SEO } from '@/components/SEO';

export function PrivacyPage() {
  return (
    <>
      <SEO
        title="Privacy Policy - Nestor HRMS"
        description="Learn how Nestor HRMS collects, uses, protects, and manages HRMS data for web, Android, and iOS services."
      />

      <div className="container mx-auto px-4 py-20 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4">Privacy Policy - Nestor HRMS</h1>
        <div className="prose prose-lg max-w-none space-y-6 text-muted-foreground">
          <p className="text-sm">Effective Date: May 24, 2026</p>
          <p>
            Nestor HRMS ("we", "our", or "us") values the privacy and security of user data.
            This Privacy Policy explains how we collect, use, share, and protect information when
            customers, employees, administrators, and authorized users access our HRMS platform,
            mobile applications for Android and iOS, websites, and related services.
          </p>
          <p>
            Nestor HRMS is a software-as-a-service platform used by organizations to manage human
            resources operations. In many cases, the organization that provides user access to
            Nestor HRMS is responsible for deciding what employee data is collected and how it is
            used. Nestor HRMS processes that data to deliver the contracted services and to support
            applicable HR, payroll, employment, tax, labor, and statutory compliance requirements.
          </p>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">1. Information We Collect</h2>
            <p>
              To provide HRMS functionalities and employee management services, we may collect and
              process the following information:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Employee name, profile details, employee identifiers, and employment records</li>
              <li>Email address, phone number, business contact information, and emergency contact information</li>
              <li>Attendance, time, shift, leave, holiday, and absence records</li>
              <li>Payroll, compensation, benefits, reimbursement, tax, and statutory contribution details</li>
              <li>Performance, recruitment, onboarding, training, workflow, and approval information</li>
              <li>Device, browser, app version, IP address, login, session, and security audit information</li>
              <li>Location data only if enabled by the organization for attendance, geofencing, or workforce verification</li>
              <li>Uploaded documents, profile photos, identity or work-related records, and other HR documents</li>
              <li>Support requests, feedback, communication preferences, and service usage information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">2. Purpose of Data Collection</h2>
            <p>
              We collect and process user information strictly for application functionality, HR
              operations, service delivery, and authorized business purposes, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Attendance, shift, leave, holiday, and workforce management</li>
              <li>Payroll processing, compensation management, tax reporting, and statutory compliance</li>
              <li>Employee self-service features, document handling, and profile management</li>
              <li>Recruitment, onboarding, performance, learning, workflows, notifications, and approvals</li>
              <li>Role-based access control, authentication, account security, and fraud prevention</li>
              <li>Customer support, troubleshooting, service improvement, analytics, and audit logs</li>
              <li>Compliance with labor, employment, payroll, tax, benefits, data protection, and recordkeeping obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">3. Data Sharing</h2>
            <p>
              We do not sell, rent, or share personal user data with third parties for marketing or
              advertising purposes. User data may only be accessed or shared:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>By authorized organizational administrators and users based on assigned roles and permissions</li>
              <li>For statutory, legal, payroll, tax, benefits, accounting, audit, or compliance requirements</li>
              <li>With secure infrastructure, hosting, analytics, notification, payment, or support providers necessary to operate the service</li>
              <li>When required by applicable law, court order, regulator, government authority, or lawful legal process</li>
              <li>With the customer organization or its authorized representatives as part of the HRMS service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">4. Data Security</h2>
            <p>
              We implement industry-standard technical, administrative, and organizational safeguards
              to protect user information against unauthorized access, alteration, disclosure, loss,
              misuse, or destruction. These safeguards may include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption in transit and at rest where appropriate</li>
              <li>Role-based access controls, authentication, authorization, and access logging</li>
              <li>Secure development practices, vulnerability management, and infrastructure monitoring</li>
              <li>Backups, availability safeguards, and incident response procedures</li>
              <li>Employee and vendor confidentiality obligations and security awareness practices</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">5. Compliance</h2>
            <p>
              Nestor HRMS is designed to support customers in meeting applicable statutory, labor,
              payroll, tax, social security, benefits, employment, recordkeeping, and data protection
              obligations across countries, states, provinces, and local jurisdictions where the
              platform is used. Customers remain responsible for configuring and using the service in
              accordance with laws that apply to their organization, workforce, and region.
            </p>
            <p>
              Depending on where users and customer organizations are located, relevant privacy and
              data protection laws may include GDPR, UK GDPR, India's Digital Personal Data Protection
              Act, applicable United States federal and state privacy laws, Canada's privacy laws, and
              other regional privacy, employment, payroll, and labor regulations. We work to process
              personal data in a manner consistent with applicable contractual, legal, and security
              obligations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">6. Mobile App Permissions</h2>
            <p>
              The Nestor HRMS mobile app for Android and iOS may request permissions only when needed
              for app functionality. These permissions may include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Camera access for profile photos, document capture, or attendance verification features</li>
              <li>Photo, media, file, or storage access for uploading and handling work-related documents</li>
              <li>Location access for attendance, geofencing, shift verification, or field workforce features, only if enabled by the organization</li>
              <li>Notification permissions for approvals, reminders, announcements, attendance alerts, and workflow updates</li>
              <li>Biometric or device security features where enabled by the user and supported by the device</li>
            </ul>
            <p>
              Permissions are used strictly for app functionality. Users may manage permissions through
              Android or iOS device settings, although disabling certain permissions may limit specific
              HRMS features.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">7. User Consent and Choices</h2>
            <p>
              By using the application and services, users consent to the collection and use of
              information as described in this Privacy Policy, subject to applicable law and the
              instructions of their organization. Users may request access, correction, update,
              portability, restriction, objection, or deletion of personal information where these
              rights are available under applicable law.
            </p>
            <p>
              Employees should usually submit HR data requests to their employer or organization
              administrator, because the organization controls most employee records in Nestor HRMS.
              We will support customer organizations in responding to lawful user requests where
              required.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">8. Data Retention</h2>
            <p>
              We retain personal data for as long as necessary to provide the services, meet contractual
              commitments, support HR and payroll operations, maintain audit trails, resolve disputes,
              and comply with legal, statutory, tax, employment, and recordkeeping requirements.
              Retention periods may vary by country, state, province, organization policy, data type,
              and legal obligation.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">9. International Data Processing</h2>
            <p>
              Because Nestor HRMS is a SaaS platform, data may be stored or processed using secure
              cloud infrastructure and service providers located in different regions. Where personal
              data is transferred internationally, we use appropriate contractual, technical, and
              organizational measures intended to protect the data in accordance with applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">10. Children's Privacy</h2>
            <p>
              Nestor HRMS is intended for workforce, business, and HR administration use. It is not
              directed to children and should not be used by individuals below the minimum age required
              by applicable law unless the use is lawful, authorized by the organization, and related
              to employment, internship, apprenticeship, or similar workforce administration.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our services,
              mobile applications, legal requirements, or security practices. The updated version will
              be posted on this page with a revised effective date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">12. Contact Us</h2>
            <p>
              If you have any questions regarding this Privacy Policy, please contact:
            </p>
            <p>
              Nestor HRMS
              <br />
              Privacy and Support Team
              <br />
              Email: umesh.agarwal@nestorindia.com
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
